import React, { useMemo, useRef, useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { CalendarDays, ChevronDown } from 'lucide-react';
import 'react-day-picker/dist/style.css';
import styles from './DateRangePicker.module.css';

const DateRangePicker = ({ startDate, endDate, onChange, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [draftRange, setDraftRange] = useState(undefined);
  const containerRef = useRef(null);

  const range = useMemo(
    () => ({
      from: startDate || undefined,
      to: endDate || undefined,
    }),
    [startDate, endDate],
  );

  useEffect(() => {
    const handler = (event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const toggleOpen = () => {
    setOpen((previous) => {
      const next = !previous;
      if (next) setDraftRange(range);
      return next;
    });
  };

  const rangeLabel = startDate && endDate
    ? `${format(startDate, 'dd.MM.yyyy')} — ${format(endDate, 'dd.MM.yyyy')}`
    : 'Выберите период';

  const wrapperClass = [styles.wrapper, className].filter(Boolean).join(' ');

  return (
    <div className={wrapperClass} ref={containerRef}>
      <button
        type="button"
        className={styles.dateButton}
        onClick={toggleOpen}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <CalendarDays size={18} className={styles.calendarIcon} />
        <span className={styles.dateValue}>{rangeLabel}</span>
        <ChevronDown size={18} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} />
      </button>

      {open && (
        <div className={styles.popover} role="dialog" aria-label="Выбор периода">
          <div className={styles.popoverHeader}>
            <strong>Выберите период</strong>
            <span>Сначала начальную, затем конечную дату</span>
          </div>
          <DayPicker
            mode="range"
            selected={draftRange}
            onSelect={setDraftRange}
            locale={ru}
            weekStartsOn={1}
            defaultMonth={range.from || range.to || new Date()}
            showOutsideDays
          />
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => {
                onChange(null, null);
                setDraftRange(undefined);
                setOpen(false);
              }}
            >
              Сбросить
            </button>
            <button
              type="button"
              className={`${styles.actionBtn} ${styles.actionPrimary}`}
              disabled={!draftRange?.from || !draftRange?.to}
              onClick={() => {
                onChange(draftRange.from, draftRange.to);
                setOpen(false);
              }}
            >
              Применить
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
